/**
 * 模型网关把 agent（代码执行沙箱）的内部事件协议直接写进了回复正文：
 *
 *   ```python?code_reference&code_event_index=1   执行过的代码
 *   ```text?code_stdout&code_event_index=1        它的 stdout / traceback
 *   [名称.xlsx](sandbox:/mnt/uploads/名称.xlsx)   沙箱内的路径，浏览器打不开
 *   [file-tag: code-generated-file-<uuid>]        产物占位符，没有任何组件认识它
 *
 * 这些都不是写给人看的 markdown。前两种会被渲染成一张张独立的代码卡，落在「执行了代码」
 * 过程块**外面**（过程块头部的展开/收起管不到它们，用户点收起后发现整屏代码还在）；
 * 后两种更糟：正文里出现一个点了没反应的下载链接，外加一行裸露的占位符。
 *
 * 本模块只做纯文本解析：
 *   1. `splitAgentCodeEvents` 把代码事件从正文里拆出来，交给 structuredOutput 归进过程块；
 *   2. `stripAgentProtocolArtifacts` 清掉正文里的沙箱死链接与占位符（产物文件由正文下方的
 *      生成文件卡片作为唯一入口，与 `stripLucknotePreviewLinks` 的产品口径一致）。
 */

export type AgentFileRef = {
	name?: string | null;
	id?: string | null;
	url?: string | null;
};

export type AgentCodeEventKind = 'reference' | 'stdout' | 'stderr';

export type AgentCodeEvent = {
	/** 同一次执行的代码与输出共享这个序号（`code_event_index=N`）。 */
	index: string;
	kind: AgentCodeEventKind;
	/** 代码块信息串里的语言名去掉协议参数后的部分（python / text …）。 */
	lang: string;
	code: string;
};

/** structuredOutput 需要的 token 形状（与 OutputDetailToken 结构一致，避免循环依赖）。 */
export type AgentCodeEventToken = {
	summary: string;
	text: string;
	attributes: {
		type: 'code_interpreter';
		done: string;
		duration?: string;
		/** 正文回放的事件：只提供详情，不参与过程头部的事件计数。 */
		transcript?: string;
	};
};

export type AgentTextSegment = { type: 'text'; text: string };
export type AgentCodeSegment = { type: 'code-event'; event: AgentCodeEvent };
export type AgentSegment = AgentTextSegment | AgentCodeSegment;

/** 正文里一整块 ```lang?code_reference&code_event_index=N … ``` */
const CODE_EVENT_INFO = /^([^\s`?]*)\?code_(reference|stdout|stderr)((?:&[^\s`]*)?)$/i;
const FENCE_LINE = /^```([^\s`]*)[ \t]*$/;
const CLOSING_FENCE_LINE = /^[ \t]*```[ \t]*$/;
const EVENT_INDEX = /(?:^|&)code_event_index=([^&]+)/i;
/** 代码块信息串里的协议参数（没有 index 时也要清掉）。 */
const PROTOCOL_INFO_SUFFIX = /\?code_(?:reference|stdout|stderr)(?:&[^\s`]*)?/gi;

const DEFAULT_EVENT_LANG = 'python';

function stripLineEndings(line: string): string {
	return line.endsWith('\r') ? line.slice(0, -1) : line;
}

/** 解析 ```info 行；不是代码执行事件协议时返回 null。 */
function parseCodeEventInfo(
	infoLine: string
): { lang: string; kind: AgentCodeEventKind; index: string } | null {
	const fence = FENCE_LINE.exec(infoLine.trim());
	if (!fence) return null;

	const info = CODE_EVENT_INFO.exec(fence[1]);
	if (!info) return null;

	return {
		lang: info[1] || DEFAULT_EVENT_LANG,
		kind: info[2].toLowerCase() as AgentCodeEventKind,
		index: EVENT_INDEX.exec(info[3] ?? '')?.[1] ?? ''
	};
}

/**
 * 把正文按「普通文本 / 代码执行事件」切成有序片段。
 *
 * 未闭合的尾块（流式输出到一半）同样算事件：否则代码卡会先在正文里闪一下再跳走。
 */
export function splitAgentCodeEvents(text: string): AgentSegment[] {
	if (!text || !text.includes('?code_')) {
		return [{ type: 'text', text }];
	}

	const lines = text.split('\n').map(stripLineEndings);
	const segments: AgentSegment[] = [];
	let buffer: string[] = [];

	const flushText = () => {
		if (!buffer.length) return;
		segments.push({ type: 'text', text: buffer.join('\n') });
		buffer = [];
	};

	let index = 0;
	while (index < lines.length) {
		const event = parseCodeEventInfo(lines[index]);
		if (!event) {
			buffer.push(lines[index]);
			index += 1;
			continue;
		}

		const codeLines: string[] = [];
		let cursor = index + 1;
		let closed = false;
		for (; cursor < lines.length; cursor += 1) {
			if (CLOSING_FENCE_LINE.test(lines[cursor])) {
				closed = true;
				break;
			}
			codeLines.push(lines[cursor]);
		}

		flushText();
		segments.push({
			type: 'code-event',
			event: { ...event, code: codeLines.join('\n') }
		});

		index = closed ? cursor + 1 : cursor;
	}

	flushText();
	return segments.length ? segments : [{ type: 'text', text }];
}

/** 语言名 → 代码卡标题：`python` 大写没意义，`text` 当成纯文本输出。 */
function codeFence(lang: string, code: string): string {
	return `\`\`\`${lang || DEFAULT_EVENT_LANG}\n${code}\n\`\`\``;
}

/**
 * 正文 → 剩下的正文 + 每个代码事件一枚 `code_interpreter` token。
 *
 * 同一次执行（同一 index）的代码与 stdout 合成一个 token：渲染出来是「代码卡 + 输出卡」，
 * 与原生代码解释器一致，但整组都落在过程块里，跟着「执行了代码」一起收起。
 */
export function splitAgentCodeEventMessage(text: string): {
	text: string;
	tokens: AgentCodeEventToken[];
} {
	const segments = splitAgentCodeEvents(text);
	// 没有回放事件时必须原样返回：下面的空行收敛会动到普通代码块里的空行。
	if (!segments.some((segment) => segment.type === 'code-event')) {
		return { text, tokens: [] };
	}

	const blocksByIndex = new Map<string, string[]>();
	const textParts: string[] = [];

	for (const segment of segments) {
		if (segment.type === 'text') {
			textParts.push(segment.text);
			continue;
		}

		// 同一次执行的代码与 stdout 是一对：按 `code_event_index` 归并，不要求两者相邻
		// （正文里两块之间通常夹着空行，相邻判据会把一次执行拆成两张卡）。
		const event = segment.event;
		if (!event.code.trim()) continue;

		const key = event.index || String(blocksByIndex.size);
		const blocks = blocksByIndex.get(key) ?? [];
		if (!blocksByIndex.has(key)) {
			blocksByIndex.set(key, blocks);
		}
		blocks.push(codeFence(event.lang, event.code));
	}

	const tokens: AgentCodeEventToken[] = [];
	for (const blocks of blocksByIndex.values()) {
		if (!blocks.length) continue;
		tokens.push({
			summary: 'Executed code',
			text: blocks.join('\n\n'),
			attributes: { type: 'code_interpreter', done: 'true', transcript: 'true' }
		});
	}

	return {
		text: textParts
			.join('\n')
			.replace(/[ \t]+\n/g, '\n')
			.replace(/\n{3,}/g, '\n\n')
			.trim(),
		tokens
	};
}

/**
 * 内容路径（没有结构化 output 的老消息）兜底：只把信息串里的协议参数去掉，
 * 让代码卡标题回到 `python` / `text`，不至于把 `?code_reference&code_event_index=1` 当语言名。
 */
export function normalizeAgentCodeEventFences(text: string): string {
	if (!text || !text.includes('?code_')) return text;
	return text.replace(PROTOCOL_INFO_SUFFIX, '');
}

/**
 * 纯文本管线（TTS 朗读、大纲摘要、自定义动作）里的代码回放直接整段丢掉：
 * 它是过程块里 tool_calls 行的重复内容，念出来/摘出来都是噪音。
 * 结构化渲染不走这里（见 `splitAgentCodeEventMessage`，代码要留在可收起的过程块里）。
 */
export function stripAgentCodeEvents(text: string): string {
	if (!text || !text.includes('?code_')) return text;

	const segments = splitAgentCodeEvents(text);
	if (!segments.some((segment) => segment.type === 'code-event')) return text;

	return segments
		.filter((segment): segment is AgentTextSegment => segment.type === 'text')
		.map((segment) => segment.text)
		.join('\n')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

const FILE_TAG = /\[file-tag:[^\]\n]*\]/gi;
const SANDBOX_MARKDOWN_LINK = /\[([^\]\n]*)\]\(\s*sandbox:[^)\n]*\)/gi;
const SANDBOX_HTML_LINK = /<a\b[^>]*href=["']sandbox:[^"']*["'][^>]*>([\s\S]*?)<\/a>/gi;

/**
 * 清掉正文里的沙箱文件死链接与产物占位符。
 *
 * 判据：这条消息只要带了产物文件，正文下方就有生成文件卡片（预览 + 下载），
 * `sandbox:` 链接一律删掉——它指向的是浏览器沙箱里的 `/mnt/uploads/…`，点开就是空白页，
 * 而且文件名经常和真正存下来的文件对不上（模型自己编的名字），留着只会误导。
 * 没有产物文件时退化成纯文本文件名：不留可点的假链接，但保留「助手说过要交付什么」的信息。
 */
export function stripAgentFileArtifacts(text: string, files: AgentFileRef[] = []): string {
	if (!text) return text;

	const hasFiles = (files ?? []).filter(Boolean).length > 0;
	let changed = false;

	const replaceLink = (label: string): string => {
		changed = true;
		const keepLabel = !hasFiles && label.trim().length > 0;
		return keepLabel ? label : '';
	};

	let output = text.replace(SANDBOX_MARKDOWN_LINK, (_match, label: string) =>
		replaceLink(String(label ?? ''))
	);
	output = output.replace(SANDBOX_HTML_LINK, (_match, inner: string) =>
		replaceLink(
			String(inner ?? '')
				.replace(/<[^>]*>/g, '')
				.trim()
		)
	);
	output = output.replace(FILE_TAG, () => {
		changed = true;
		return '';
	});

	if (!changed) return text;

	return output
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/**
 * 正文清理入口：代码事件协议 + 沙箱文件产物占位符。
 * 代码事件本身由 structuredOutput 消费（归到过程块），这里只处理「留在正文里就是噪音」的部分。
 */
export function stripAgentProtocolArtifacts(text: string, files: AgentFileRef[] = []): string {
	return stripAgentFileArtifacts(normalizeAgentCodeEventFences(text), files);
}
